import {Injectable, OnDestroy} from '@angular/core';
import {Subscription} from 'rxjs';
import {ToolsService} from '../../../services/tools.service';
import {BookingV2DataService} from './booking-v2-data.service';
import {BookingMetadata} from '../types/booking-metadata';
import {UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import {filter} from 'rxjs/operators';
import {BookingFormFieldTypeEnum, SurveyAdditionalFieldsEnum} from '../enums/booking-enums';
import {Profile} from '../../../types/MyPages';

@Injectable({
  providedIn: 'any',
})
export class BookingV2SurveyService implements OnDestroy {
  public surveyForm: UntypedFormGroup;
  public phoneNumberFieldKey: string;
  private subscriptions: Subscription[] = [];
  private metadata: BookingMetadata[];
  private profile: Profile;

  constructor(
    private tools: ToolsService,
    private bookingData: BookingV2DataService,
  ) {
    this.setListeners();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public resetAll(): void {
    this.surveyForm = null;
    this.metadata = null;
  }

  private handleProviderMeta(metadata: BookingMetadata[]): void {
    this.metadata = metadata;
    this.buildSurveyForm();
  }

  private buildSurveyForm(): void {
    const group: any = {};
    this.phoneNumberFieldKey = null;
    this.metadata.forEach((formField) => {
      const defaultValue =
        formField.type === BookingFormFieldTypeEnum.BOOLEAN ? formField.default : formField.default || '';
      if (formField.type === BookingFormFieldTypeEnum.PHONE_NUMBER) {
        group[SurveyAdditionalFieldsEnum.COUNTRY_CODE] = new UntypedFormControl('+46', [Validators.required]);
        group[formField.key] = new UntypedFormControl(defaultValue, [
          Validators.required,
          Validators.minLength(9),
          Validators.maxLength(9),
          Validators.pattern(/^7[0-9]{8}$/),
        ]);
        this.phoneNumberFieldKey = formField.key;
      } else {
        group[formField.key] = new UntypedFormControl(defaultValue, this.mapFieldValidators(formField));
      }
    });
    group[SurveyAdditionalFieldsEnum.GDPR] = new UntypedFormControl(false, [Validators.requiredTrue]);
    this.surveyForm = new UntypedFormGroup(group);
  }

  private mapFieldValidators(data: BookingMetadata): Array<ValidatorFn> {
    const validationRules: Array<ValidatorFn> = [];
    Object.keys(data.validationRules)
      .filter((key) => data.validationRules[key])
      .forEach((key) => {
        switch (key) {
          case 'required':
            validationRules.push(Validators.required);
            if (data.type === BookingFormFieldTypeEnum.BOOLEAN) {
              validationRules.push(Validators.requiredTrue);
            }
            break;
          case 'minLength':
            validationRules.push(Validators.minLength(data.validationRules[key]));
            break;
          case 'maxLength':
            validationRules.push(Validators.maxLength(data.validationRules[key]));
            break;
          case 'regex':
            validationRules.push(Validators.pattern(data.validationRules[key]));
            break;
        }
      });
    return validationRules;
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.bookingData.providerMeta$
        .pipe(filter((res) => res !== null))
        .subscribe((result) => this.handleProviderMeta(result)),
      this.bookingData.profile$.subscribe((result) => this.handleProfile(result)),
    );
  }

  private handleProfile(result: Profile) {
    this.profile = result;
    this.updateSurveyFormWithProfileData();
  }

  private updateSurveyFormWithProfileData() {
    if (!this.profile) {
      return;
    }
    const controls = this.surveyForm.controls;
    Object.keys(controls).forEach((key) => {
      if (this.profile[key]) {
        controls[key].setValue(this.profile[key]);
        controls[key].disable();
      }
      const personalNumberField = this.metadata.find(
        (field) => field.type === BookingFormFieldTypeEnum.PERSONAL_NUMBER,
      );
      if (!this.tools.isNil(personalNumberField)) {
        controls[personalNumberField.key].setValue(this.profile.long_pno);
        controls[personalNumberField.key].disable();
      }
    });
  }
}
